import React from "react";
import { useMapEvents } from "react-leaflet";
import { LeafletMouseEvent } from "leaflet";
import { fromLatLng } from "../../../utils/geo";
import { useTrapsiteEditorContext } from "./TrapsiteEditorContext";

/**
 * Turns right-clicks on the map into context-menu opens, and everything else
 * into a dismissal.
 *
 * Registering a map-level `contextmenu` handler is also what suppresses the
 * native browser menu: Leaflet only calls preventDefault for that event when
 * something is actually listening (Map._fireDOMEvent).
 */
const MapContextMenuController: React.FC = () => {
  const editor = useTrapsiteEditorContext();

  const map = useMapEvents({
    contextmenu(e: LeafletMouseEvent) {
      if (!editor.enabled) return;

      const target = e.originalEvent?.target as HTMLElement | null;

      // The permanent marker labels have `pointer-events: auto`, and a tooltip
      // is a non-interactive layer - so Leaflet never treats it as an event
      // target and the click bubbles to the container, arriving here with the
      // LABEL's latlng rather than the pin's. Marker.tsx stamps the id onto the
      // tooltip element so we can recover which site was actually clicked.
      const tooltip = target?.closest?.(
        ".leaflet-tooltip",
      ) as HTMLElement | null;
      const trapsiteId = tooltip?.dataset?.trapsiteId;
      if (trapsiteId) {
        editor.openMenu(
          { x: e.originalEvent.clientX, y: e.originalEvent.clientY },
          { kind: "trapsite", id: trapsiteId },
        );
        return;
      }

      // Marker.tsx handles its own pins and sets bubblingMouseEvents={false},
      // so this should be unreachable for a marker icon. Kept as a guard
      // because that behaviour is version-sensitive: without it, a regression
      // would silently offer "Create" at the pin's own coordinate.
      if (target?.closest?.(".leaflet-marker-icon")) return;

      editor.openMenu(
        { x: e.originalEvent.clientX, y: e.originalEvent.clientY },
        { kind: "map", gps: fromLatLng(e.latlng) },
      );
    },
    // antd's own outside-click handling cannot see a Leaflet-driven pan, so
    // dismissal is wired to the map's own events too.
    click: () => editor.closeMenu(),
    mousedown: () => editor.closeMenu(),
    movestart: () => editor.closeMenu(),
    zoomstart: () => editor.closeMenu(),
  });

  void map;
  return null;
};

export default MapContextMenuController;
